import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useCallback
} from "react";
import authService, { User } from "./authService";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Spinner } from "@chakra-ui/react";
    
// Define context types
interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
}

// Create auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Props interface
interface AuthProviderProps {
    children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Check for existing token and get user on mount
    useEffect(() => {
        let isMounted = true;
        const checkAuthStatus = async () => {
            try {
                if (authService.isAuthenticated()) {
                    const currentUser = await authService.getCurrentUser();
                    if (isMounted) {
                        setUser(currentUser);
                    }
                } else {
                    // Jika tidak ada token dan bukan di halaman login, redirect ke login
                    if (isMounted && location.pathname !== '/login') {
                        navigate('/login', { replace: true });
                    }
                }
            } catch (err) {
                console.error("Auth check error:", err);
                authService.logout();
                if (isMounted && location.pathname !== '/login') {
                    navigate('/login', { replace: true });
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        checkAuthStatus();
        
        return () => {
            isMounted = false;
        };
    }, [navigate, location.pathname]);

    // Login function
    const login = async (email: string, password: string) => {
        setLoading(true);
        setError(null);
        try {
            await authService.login({ email, password });
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
            navigate('/', { replace: true });
        } catch (err: any) {
            setError(err.response?.data?.detail || "Login gagal. Silakan coba lagi.");
            console.error("Login error:", err);
        } finally {
            setLoading(false);
        }
    };

    // Logout function
    const logout = () => {
        authService.logout();
        setUser(null);
        setError(null);
        navigate('/login', { replace: true });
    };


    // Context value with useMemo to prevent unnecessary re-renders
    const value = React.useMemo(
        () => ({
            user,
            loading,
            error,
            login,
            logout,
        }),
        [user, loading, error, login, logout]
    );

    if (loading) {
        // Misalnya, menampilkan spinner di tengah layar.
        return (
            <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner
                    size="xl"
                    thickness="4px"
                    speed="0.65s"
                    color="blue.500"
                />
                <div style={{ marginLeft: '10px' }}>Loading...</div>
            </div>
        );
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;

};

// Custom hook for using auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

// Auth protected route component
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, loading } = useAuth();

    // Masih loading, jangan render apapun
    if (loading) {
        return null;
    }

    // Sudah selesai loading tapi tidak ada user, redirect ke login
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Ada user, render children
    return <>{children}</>;
};
